/**
 * Members strip — the pure math under ClubMembersStrip.svelte (spec 2b, C5).
 *
 * One row per member, daily distance bars on CALENDAR-WEEK pages (build 15: the first
 * weekday comes from the locale, Sunday when the engine cannot say; the build 14 rolling
 * 7 days started on whatever weekday today minus six was, "the week starts on Tuesday"),
 * totals per page, and the reactions that are still fresh. The pages cover at least the
 * 14 days C5 delivers own swims for: 2 or 3 weeks, so nothing the rolling strip showed drops.
 * Everything here takes `now` explicitly so a fixture renders the same on any
 * day and the tests never read the clock.
 *
 * Non-gamified rule (vision doc, C5): nothing here ranks. Members sort self
 * first, then by name; bars are distances scaled to the club's biggest day so
 * rows read side by side, never a leaderboard.
 */
import type { ClubMember, ClubReaction, ClubSnapshot, ClubSwim, ClubWeekTotals, PresetEmoji } from "./snapshot";
import { PRESET_EMOJI, REACTION_TTL_MS } from "./snapshot";

export const DAYS_PER_WEEK = 7;
/** The own-history window C5 carries (CompanionStore.historyDays): the pages must cover it. */
export const WINDOW_DAYS = 14;
/** By weeks before this week: 0 names the page holding today. Older pages read their dates. */
export const WEEK_LABELS = ["This week", "Last week", "2 weeks ago"] as const;
/** Sunday-first weekday letters, indexed by Date#getDay. */
export const WEEKDAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"] as const;

/** Self first, then by name (localeCompare, so "Jess" < "Ryan"). Ties by id. */
export function sortMembers<T extends { id: string; name: string }>(members: readonly T[], selfId: string): T[] {
  return [...members].sort((a, b) => {
    if (a.id === selfId) return -1;
    if (b.id === selfId) return 1;
    return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
  });
}

/** Local calendar day key, "YYYY-MM-DD", from a Date or ms. */
export function localDayKey(t: Date | number): string {
  const d = typeof t === "number" ? new Date(t) : t;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** "YYYY-MM-DD" -> that local day's midnight. */
export function dayKeyDate(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Local midnight of the day `n` days before (or after) `t`, DST-safe. */
export function dayStart(t: number, n = 0): Date {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return d;
}

/** What `Intl.Locale` may expose about weeks: `getWeekInfo()` (current engines) or the older
 *  `weekInfo` getter (Safari 17). `firstDay` is ISO: 1 = Monday … 7 = Sunday. */
export interface WeekInfoSource {
  getWeekInfo?: () => { firstDay?: number } | undefined;
  weekInfo?: { firstDay?: number };
}

/** The locale's first weekday (1-7, 7 = Sunday); Sunday when the engine has no week info. */
export function firstWeekdayOf(locale: WeekInfoSource | null | undefined): number {
  let day: unknown;
  try {
    day = locale?.getWeekInfo?.()?.firstDay ?? locale?.weekInfo?.firstDay;
  } catch {
    day = undefined;
  }
  return typeof day === "number" && Number.isInteger(day) && day >= 1 && day <= 7 ? day : 7;
}

/** `firstWeekdayOf(new Intl.Locale(language))`; a missing or malformed tag reads Sunday. */
export function firstWeekday(language: string | undefined): number {
  if (!language) return 7;
  try {
    return firstWeekdayOf(new Intl.Locale(language) as unknown as WeekInfoSource);
  } catch {
    return 7;
  }
}

export interface DayColumn {
  /** "YYYY-MM-DD" local. */
  key: string;
  /** Weekday letter for THAT day. */
  letter: string;
  /** Day of the month: the column reads "T 8". */
  dayNumber: number;
  isToday: boolean;
  /** After today: dimmed and inert (build 15). */
  isFuture: boolean;
}

export interface WeekPage {
  /** 0 = the oldest page; the last index holds today (the page the strip opens on). */
  index: number;
  /** "This week", "Last week", "2 weeks ago", then the dates: "Aug 9 – 15" (build 15, for Scott's veto). */
  label: string;
  days: DayColumn[];
}

/** Local midnight of the first day (ISO `firstDay`, 7 = Sunday) of the week containing `t`. */
export function weekStart(t: number, firstDay: number): Date {
  const d = dayStart(t);
  d.setDate(d.getDate() - ((d.getDay() - (firstDay % 7) + 7) % 7));
  return d;
}

/** "Aug 9 – 15", or "Aug 30 – Sep 5" across a month edge. */
function rangeLabel(days: readonly DayColumn[]): string {
  const a = dayKeyDate(days[0].key);
  const b = dayKeyDate(days[days.length - 1].key);
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: "short" });
  return a.getMonth() === b.getMonth() ? `${month(a)} ${a.getDate()} – ${b.getDate()}` : `${month(a)} ${a.getDate()} – ${month(b)} ${b.getDate()}`;
}

/** Calendar weeks, oldest first, from the week holding the window's first day (today minus 13)
 *  through the week holding today: 2 or 3 pages, so every swim in the 14-day window has a column.
 *  VP round 1: the date row pages back without limit, so a `selectedDay` older than that joins as
 *  ONE more page (its own week, named by its dates): the strip always holds the selected day. */
export function weekPages(now: number, firstDay = 7, selectedDay: string | null = null): WeekPage[] {
  const todayKey = localDayKey(now);
  const thisWeek = weekStart(now, firstDay);
  const cursor = weekStart(dayStart(now, -(WINDOW_DAYS - 1)).getTime(), firstDay);
  const starts: Date[] = [];
  if (selectedDay) {
    const selected = weekStart(dayKeyDate(selectedDay).getTime(), firstDay);
    if (localDayKey(selected) < localDayKey(cursor)) starts.push(selected);
  }
  for (; localDayKey(cursor) <= localDayKey(thisWeek); cursor.setDate(cursor.getDate() + DAYS_PER_WEEK)) starts.push(new Date(cursor));
  return starts.map((start, index) => {
    const days: DayColumn[] = [];
    for (let i = 0; i < DAYS_PER_WEEK; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = localDayKey(d);
      days.push({ key, letter: WEEKDAY_LETTERS[d.getDay()], dayNumber: d.getDate(), isToday: key === todayKey, isFuture: key > todayKey });
    }
    const weeksAgo = Math.round((thisWeek.getTime() - start.getTime()) / (DAYS_PER_WEEK * 86_400_000));
    return { index, label: weeksAgo < WEEK_LABELS.length ? WEEK_LABELS[weeksAgo] : rangeLabel(days), days };
  });
}

/** The page holding `dayKey`, or -1 (a day older than the strip). */
export function pageIndexOf(pages: readonly WeekPage[], dayKey: string): number {
  return pages.findIndex((p) => p.days.some((d) => d.key === dayKey));
}

/** The page the strip shows for the date row's day: that day's week when the strip has it, else
 *  this week. Re-run whenever the weeks change: the page COUNT is 2 on the day before the first
 *  weekday and 3 otherwise, so an index kept across midnight can point at a page that is gone. */
export function pageFor(pages: readonly WeekPage[], selectedDay: string | null): number {
  const i = selectedDay ? pageIndexOf(pages, selectedDay) : -1;
  return i >= 0 ? i : pages.length - 1;
}

export interface DayBar {
  day: DayColumn;
  /** Sum of the member's swims that day, metres. 0 = no swim. */
  distanceM: number;
  /** Swims on that day, newest first. Empty = no bar to tap. */
  swims: ClubSwim[];
}

/** Swims bucketed by local day. */
export function swimsByDay(swims: readonly ClubSwim[]): Map<string, ClubSwim[]> {
  const out = new Map<string, ClubSwim[]>();
  for (const s of [...swims].sort((a, b) => Date.parse(b.date) - Date.parse(a.date))) {
    const key = localDayKey(Date.parse(s.date));
    const list = out.get(key);
    if (list) list.push(s);
    else out.set(key, [s]);
  }
  return out;
}

/** The member's bars for one page. */
export function barsFor(member: ClubMember, page: WeekPage): DayBar[] {
  const byDay = swimsByDay(member.swims);
  return page.days.map((day) => {
    const swims = byDay.get(day.key) ?? [];
    return { day, distanceM: swims.reduce((acc, s) => acc + s.distanceM, 0), swims };
  });
}

/** Totals for the member's swims inside one page. */
export function weekTotalsFor(member: ClubMember, page: WeekPage): ClubWeekTotals {
  const keys = new Set(page.days.map((d) => d.key));
  let swims = 0;
  let distanceM = 0;
  let durationSec = 0;
  for (const s of member.swims) {
    if (!keys.has(localDayKey(Date.parse(s.date)))) continue;
    swims += 1;
    distanceM += s.distanceM;
    durationSec += s.durationSec;
  }
  return { swims, distanceM, durationSec };
}

/** Local midnight of the Monday that starts the week containing `t`. */
export function startOfWeek(t: number): Date {
  return weekStart(t, 1);
}

/** C5 `weekTotals` as the native builder defines it: the calendar week, Monday
 *  00:00 local through `now`. NOT rendered by the strip (its pages follow the locale's week);
 *  exported so the fixture test and club-sync's builder agree on one number. */
export function mondayWeekTotals(member: ClubMember, now: number): ClubWeekTotals {
  const from = localDayKey(startOfWeek(now));
  const to = localDayKey(now);
  let swims = 0, distanceM = 0, durationSec = 0;
  for (const s of member.swims) {
    const key = localDayKey(Date.parse(s.date));
    if (key < from || key > to) continue;
    swims += 1; distanceM += s.distanceM; durationSec += s.durationSec;
  }
  return { swims, distanceM, durationSec };
}

/** The club's biggest day across every member and both pages: the shared bar
 *  scale, so 2,000 m is the same height on every row. Never below 1. */
export function maxDayDistance(members: readonly ClubMember[], pages: readonly WeekPage[]): number {
  let max = 0;
  for (const m of members) {
    for (const p of pages) {
      for (const bar of barsFor(m, p)) max = Math.max(max, bar.distanceM);
    }
  }
  return Math.max(1, max);
}

/** Reactions still inside the 24 h window at `now`. A reaction from the future
 *  (clock skew between phones) is kept: it is fresh, not stale. */
export function freshReactions(reactions: readonly ClubReaction[], now: number): ClubReaction[] {
  return reactions.filter((r) => now - Date.parse(r.createdAt) < REACTION_TTL_MS);
}

export interface ReactionSummary {
  /** Distinct emoji in C5 order of first appearance, with counts. */
  counts: { emoji: PresetEmoji; count: number }[];
  total: number;
  /** The viewer's own reaction on this swim, if any. */
  mine: PresetEmoji | null;
}

/** One swim's reactions, or a whole day's (`swimId` as an array: the bar for a
 *  day with two swims carries both swims' reactions). */
export function reactionsForSwim(reactions: readonly ClubReaction[], swimId: string | readonly string[], selfId: string): ReactionSummary {
  const ids = new Set(typeof swimId === "string" ? [swimId] : swimId);
  const counts = new Map<PresetEmoji, number>();
  let mine: PresetEmoji | null = null;
  for (const r of reactions) {
    if (!ids.has(r.swimId)) continue;
    counts.set(r.emoji, (counts.get(r.emoji) ?? 0) + 1);
    if (r.memberId === selfId) mine = r.emoji;
  }
  const list = [...counts.entries()].map(([emoji, count]) => ({ emoji, count }));
  return { counts: list, total: list.reduce((a, c) => a + c.count, 0), mine };
}

/** What the club gave YOUR swim (build 15b, Scott: "silly for you to be able to emoji your own
 *  sets"): clubmates' reactions only, never the viewer's own, in C5 order; nonzero emoji only. */
export function receivedCounts(reactions: readonly ClubReaction[], swimId: string, selfId: string): { emoji: PresetEmoji; count: number }[] {
  const given = reactions.filter((r) => r.swimId === swimId && r.memberId !== selfId);
  return PRESET_EMOJI.map((emoji) => ({ emoji, count: given.filter((r) => r.emoji === emoji).length })).filter((c) => c.count > 0);
}

/** VoiceOver line for `receivedCounts`: "Reactions from your club: 👏 2, 🌊 1". */
export function receivedLabel(counts: readonly { emoji: PresetEmoji; count: number }[]): string {
  return `Reactions from your club: ${counts.map((c) => `${c.emoji} ${c.count}`).join(", ")}`;
}

/** The one emoji a bar can show for a swim (or a day of swims): the viewer's
 *  own reaction if they made one, else the most-given emoji, ties broken by
 *  C5 order. Never the first-inserted (that lied: after a 🔥 tap the bar read
 *  "💪 2" while the detail read "You 🔥 · Ryan 💪"). */
export function badgeEmoji(summary: ReactionSummary): PresetEmoji | null {
  if (summary.mine) return summary.mine;
  if (summary.counts.length === 0) return null;
  return [...summary.counts].sort(
    (a, b) => b.count - a.count || PRESET_EMOJI.indexOf(a.emoji) - PRESET_EMOJI.indexOf(b.emoji),
  )[0].emoji;
}

/** One reaction per member per swim (C5): a tap replaces the member's earlier
 *  reaction on that swim; `null` removes it. Pure; returns a new array. */
export function upsertReaction(
  reactions: readonly ClubReaction[],
  swimId: string,
  memberId: string,
  emoji: PresetEmoji | null,
  createdAt: string,
): ClubReaction[] {
  const kept = reactions.filter((r) => !(r.swimId === swimId && r.memberId === memberId));
  if (emoji === null) return kept;
  return [...kept, { swimId, memberId, emoji, createdAt }];
}

/** The strip is empty when nobody but the viewer is in the club yet. A viewer
 *  alone with swims still sees their own row; the invite card sits under it. */
export function stripIsEmpty(snapshot: ClubSnapshot): boolean {
  return snapshot.members.filter((m) => m.id !== snapshot.self).length === 0;
}

/** Lookup for "open that swim": member + swim by swim id. */
export function findSwim(snapshot: ClubSnapshot, swimId: string): { member: ClubMember; swim: ClubSwim } | null {
  for (const member of snapshot.members) {
    const swim = member.swims.find((s) => s.id === swimId);
    if (swim) return { member, swim };
  }
  return null;
}

/** `now` for a snapshot: a fixture is frozen at its own `generatedAt` so stills
 *  and tests are stable; on a shell the live clock decides what is "today". */
export function snapshotNow(snapshot: ClubSnapshot, live: boolean, clock: () => number = Date.now): number {
  return live ? clock() : Date.parse(snapshot.generatedAt);
}

/** The 14-day window C5 delivers own swims for, oldest first (the strip's pages cover it). */
export function windowDayKeys(now: number): string[] {
  return Array.from({ length: WINDOW_DAYS }, (_, i) => localDayKey(dayStart(now, i - (WINDOW_DAYS - 1))));
}
